import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash2, Radio, Info, RefreshCw, ExternalLink } from 'lucide-react';
import { Modal, Form, Input, message } from 'antd';
import api from '../api/client';
import clsx from 'clsx';

const ChannelCard = React.forwardRef(({ channel, onDelete, onCopy }, ref) => (
  <motion.div
    ref={ref}
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, scale: 0.95 }}
    className="glass-card p-6 flex flex-col justify-between group relative overflow-hidden"
  >
    <div className="absolute top-0 right-0 p-32 bg-primary/5 rounded-full blur-3xl group-hover:bg-primary/10 transition-colors" />
    
    <div className="relative z-10">
      <div className="flex justify-between items-start mb-4">
        <div className="p-3 rounded-xl bg-gradient-to-br from-primary/20 to-secondary/20 text-white border border-white/10 shadow-lg">
          <Radio size={24} />
        </div>
        <div className="flex gap-2">
          <button 
            onClick={() => onDelete(channel.id)}
            className="p-2 text-rose-400 hover:text-rose-300 hover:bg-rose-500/10 rounded-lg transition-colors"
            title="Отключить"
          >
            <Trash2 size={18} />
          </button>
        </div>
      </div>
      
      <h3 className="text-xl font-bold text-white mb-1">{channel.channel_name}</h3>
      <p className="text-primary-light font-medium mb-4">@{channel.channel_username}</p>
      
      <div className="flex items-center gap-2 text-xs text-slate-400 bg-white/5 p-2 rounded-lg border border-white/5">
        <span className="font-mono">ID: {channel.channel_id}</span>
      </div>
    </div>

    <div className="mt-6 flex items-center gap-2 text-sm text-emerald-400 relative z-10">
      <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
      Активен
    </div>
  </motion.div>
));

const Step = ({ number, title, description, icon: Icon }) => (
  <div className="flex gap-4">
    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-surface border border-white/10 flex items-center justify-center font-bold text-white relative z-10">
      {Icon ? <Icon size={20} className="text-primary" /> : number}
    </div>
    <div className="pb-8 border-l border-white/10 ml-[-29px] pl-10 last:border-0 last:pb-0">
      <h4 className="text-white font-medium mb-1">{title}</h4>
      <p className="text-slate-400 text-sm leading-relaxed">{description}</p>
    </div>
  </div>
);

export default function Channels() {
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    fetchChannels();
  }, []);

  const fetchChannels = async () => {
    try {
      const response = await api.get('/channels');
      setChannels(response.data);
    } catch (err) {
      console.error(err);
      message.error('Не удалось загрузить каналы');
    } finally {
      setLoading(false);
    }
  };

  const handleAddChannel = async (values) => {
    try {
      setAdding(true);
      const response = await api.post('/channels', values);
      const existingIndex = channels.findIndex((c) => c.id === response.data.id);
      if (existingIndex >= 0) {
        const next = [...channels];
        next[existingIndex] = response.data;
        setChannels(next);
      } else {
        setChannels([...channels, response.data]);
      }
      setIsModalVisible(false);
      form.resetFields();
      message.success('Канал успешно подключен!');
    } catch (err) {
      console.error(err);
      message.error(err?.response?.data?.error || 'Ошибка при подключении канала');
    }
    finally {
      setAdding(false);
    }
  };

  const handleDeleteChannel = async (channelId) => {
    Modal.confirm({
      title: 'Отключить канал?',
      content: 'Это действие остановит сбор статистики. История данных сохранится.',
      okText: 'Отключить',
      okType: 'danger',
      cancelText: 'Отмена',
      onOk: async () => {
        try {
          await api.delete(`/channels/${channelId}`);
          setChannels(channels.filter(c => c.id !== channelId));
          message.success('Канал отключен');
        } catch (err) {
          message.error('Ошибка при удалении');
        }
      },
    });
  };

  const botUsername = process.env.REACT_APP_TELEGRAM_BOT_USERNAME;

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-12">
        <div>
          <h1 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-slate-400 mb-2">
            Источники трафика
          </h1>
          <p className="text-slate-400 max-w-2xl">
            Подключите Telegram каналы для автоматического отслеживания подписчиков, генерации инвайт-ссылок и аналитики аудитории.
          </p>
        </div>
        <button 
          onClick={() => setIsModalVisible(true)}
          className="btn-primary flex items-center gap-2 whitespace-nowrap"
        >
          <Plus size={20} />
          Подключить канал
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence mode="popLayout">
            {channels.map((channel) => (
              <ChannelCard 
                key={channel.id} 
                channel={channel} 
                onDelete={handleDeleteChannel}
              />
            ))}
            
            {channels.length === 0 && (
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="col-span-full py-20 text-center border-2 border-dashed border-white/10 rounded-2xl"
              >
                <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-500">
                  <Radio size={32} />
                </div>
                <h3 className="text-xl font-medium text-white mb-2">Нет подключенных каналов</h3>
                <p className="text-slate-400 mb-6">Подключите свой первый Telegram канал чтобы начать сбор данных</p>
                <button 
                  onClick={() => setIsModalVisible(true)}
                  className="px-6 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
                >
                  Подключить сейчас
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      <Modal
        title={null}
        open={isModalVisible}
        footer={null}
        onCancel={() => setIsModalVisible(false)}
        width={600}
        className="dark-modal"
        closeIcon={<span className="text-slate-400 hover:text-white">✕</span>}
      >
        <div className="p-2">
          <h2 className="text-2xl font-bold text-white mb-6">Подключение Telegram канала</h2>
          
          <div className="bg-primary/10 border border-primary/20 rounded-xl p-4 mb-8">
            <h3 className="text-primary font-medium mb-4 flex items-center gap-2">
              <Info size={18} />
              Инструкция по подключению
            </h3>
            <div className="space-y-1">
              <Step 
                number={1}
                title="Добавьте нашего бота в канал"
                description={
                  botUsername
                    ? <span>Добавьте <a href={`https://t.me/${botUsername}`} target="_blank" rel="noreferrer" className="text-primary hover:underline">@{botUsername}</a> в администраторы вашего канала.</span>
                    : <span>Добавьте нашего бота в администраторы вашего канала.</span>
                }
              />
              <Step 
                number={2}
                title="Выдайте права"
                description="Дайте боту права администратора (чтобы он видел подписки, посты и мог создавать инвайт-ссылки)."
              />
            </div>
          </div>

          <Form
            form={form}
            layout="vertical"
            onFinish={handleAddChannel}
            className="space-y-4"
          >
            <div className="grid grid-cols-2 gap-4">
              <Form.Item
                label={<span className="text-slate-300">Название канала (для дашборда)</span>}
                name="channel_name"
                help={<span className="text-slate-500 text-xs">Внутреннее название для удобства</span>}
              >
                <Input className="input-field" placeholder="Например: Мой блог" />
              </Form.Item>
              <Form.Item
                label={<span className="text-slate-300">Юзернейм канала</span>}
                name="channel_username"
                rules={[{ required: true, message: 'Введите юзернейм канала' }]}
                help={<span className="text-slate-500 text-xs">Публичная ссылка на канал (без @)</span>}
              >
                <Input className="input-field" prefix={<span className="text-slate-500">@</span>} placeholder="my_channel" />
              </Form.Item>
            </div>

            <div className="flex justify-end gap-3 mt-8">
              <button 
                type="button"
                onClick={() => setIsModalVisible(false)}
                className="px-4 py-2 text-slate-400 hover:text-white transition-colors"
              >
                Отмена
              </button>
              <button 
                type="submit"
                disabled={adding}
                className="btn-primary disabled:opacity-50"
              >
                {adding ? 'Подключаем...' : 'Подключить канал'}
              </button>
            </div>
          </Form>
        </div>
      </Modal>
    </div>
  );
}
